@if (exceededDepth) {
    <p-card>
        <div class="text-orange-500">
            <i class="fa-regular fa-triangle-exclamation"></i>
            Maximale Verschachtelungstiefe ({{ 32 }}) erreicht. Bitte vereinfachen Sie den Ausdruck.
        </div>
    </p-card>
} @else if (initialized) {
    <div class="flex flex-column gap-3">

        <!-- Function selection -->
        <p-card>
            <div class="flex flex-column gap-2">
                <label class="font-semibold text-color">Format-Funktion</label>
                <p-select
                        [options]="functionGroups"
                        [group]="true"
                        optionLabel="label"
                        optionValue="value"
                        [filter]="true"
                        [filterBy]="'label,value'"
                        [showClear]="true"
                        placeholder="Funktion auswählen"
                        appendTo="body"
                        styleClass="w-full"
                        [ngModel]="selectedFunctionName"
                        (ngModelChange)="onFunctionSelected($event)">
                    <ng-template let-group pTemplate="group">
                        <div class="flex align-items-center gap-2 font-bold text-color-secondary">
                            <i class="fa-regular fa-folder"></i>
                            <span>{{ group.label }}</span>
                        </div>
                    </ng-template>
                </p-select>
                @if (activeDefinition?.description) {
                    <small class="text-color-secondary">{{ activeDefinition.description }}</small>
                }
            </div>
        </p-card>

        <!-- Parameter editor -->
        @if (activeDefinition) {
            <p-card>
                <ng-template pTemplate="header">
                    <div class="px-3 pt-3 font-semibold text-color">Parameter</div>
                </ng-template>
                @if (parameterStates.length === 0) {
                    <div class="text-color-secondary">Diese Funktion benötigt keine Parameter.</div>
                } @else {
                    <div class="flex flex-column gap-3">
                        @for (state of parameterStates; track state.definition.name) {
                            <div class="grid align-items-start">
                                <div class="col-12 md:col-3">
                                    <div class="flex flex-column">
                                        <span class="font-semibold text-color">
                                            {{ state.definition.name }}
                                            @if (state.definition.required) {
                                                <span class="text-red-500">*</span>
                                            }
                                        </span>
                                        <small class="text-color-secondary">{{ state.definition.type }}</small>
                                        @if (state.definition.description) {
                                            <small class="text-color-secondary">{{ state.definition.description }}</small>
                                        }
                                    </div>
                                </div>
                                <div class="col-12 md:col-3">
                                    <p-select
                                            [options]="modeOptions"
                                            optionLabel="label"
                                            optionValue="value"
                                            appendTo="body"
                                            styleClass="w-full"
                                            [ngModel]="state.mode"
                                            (ngModelChange)="onParameterModeChange(state, $event)">
                                    </p-select>
                                </div>
                                <div class="col-12 md:col-6">
                                    @switch (state.mode) {
                                        @case ('literal') {
                                            @if (state.definition.allowedValues && state.definition.allowedValues.length > 0) {
                                                <p-select
                                                        [options]="state.definition.allowedValues"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        placeholder="Wert auswählen"
                                                        [ngModel]="state.literalValue"
                                                        (ngModelChange)="onLiteralChange(state, $event)">
                                                </p-select>
                                            } @else {
                                                <input pInputText
                                                       class="w-full"
                                                       [placeholder]="'Literal-Wert (' + state.definition.type + ')'"
                                                       [ngModel]="state.literalValue"
                                                       (ngModelChange)="onLiteralChange(state, $event)"/>
                                            }
                                        }
                                        @case ('variable') {
                                            @if (variables && variables.length > 0) {
                                                <p-select
                                                        [options]="variables"
                                                        appendTo="body"
                                                        styleClass="w-full"
                                                        placeholder="Variable auswählen"
                                                        [filter]="true"
                                                        [ngModel]="state.variableName"
                                                        (ngModelChange)="onVariableChange(state, $event)">
                                                </p-select>
                                            } @else {
                                                <input pInputText
                                                       class="w-full"
                                                       placeholder="Variablen-Name (ohne $$)"
                                                       [ngModel]="state.variableName"
                                                       (ngModelChange)="onVariableChange(state, $event)"/>
                                            }
                                        }
                                        @case ('nested') {
                                            <div class="surface-border border-1 border-round p-3">
                                                <mvs-format-script-editor
                                                        [script]="serializeNested(state)"
                                                        [variables]="variables"
                                                        [depth]="depth + 1"
                                                        [preloadedDefinitions]="definitions"
                                                        (scriptChange)="onNestedScriptChange(state, $event)">
                                                </mvs-format-script-editor>
                                            </div>
                                        }
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }
            </p-card>
        } @else if (selectedFunctionName) {
            <p-card>
                <div class="text-red-500">
                    <i class="fa-regular fa-circle-exclamation"></i>
                    Unbekannte Funktion: {{ selectedFunctionName }}
                </div>
            </p-card>
        }

        <!-- Live preview (root only) -->
        @if (previewEnabled && activeDefinition) {
            <p-card>
                <ng-template pTemplate="header">
                    <div class="flex align-items-center justify-content-between px-3 pt-3">
                        <span class="font-semibold text-color">Vorschau</span>
                        <p-button
                                icon="fa-regular fa-play"
                                label="Vorschau ausführen"
                                [disabled]="!areMandatoryParametersFilled() || previewLoading"
                                (click)="runPreviewManually()">
                        </p-button>
                    </div>
                </ng-template>

                @if (referencedVariableNames.length > 0) {
                    <div class="flex flex-column gap-2 mb-3">
                        <span class="font-semibold text-color">Test-Werte für Variablen</span>
                        @for (varName of referencedVariableNames; track varName) {
                            <div class="grid align-items-center">
                                <div class="col-12 md:col-4">
                                    <span class="text-color">{{ varName }}</span>
                                </div>
                                <div class="col-12 md:col-8">
                                    <input pInputText
                                           class="w-full"
                                           [ngModel]="testVariableValues[varName]"
                                           (ngModelChange)="onTestVariableValueChange(varName, $event)"
                                           placeholder="Test-Wert"/>
                                </div>
                            </div>
                        }
                    </div>
                }

                @if (previewLoading) {
                    <div class="text-color-secondary">
                        <i class="fa-regular fa-spinner-third fa-spin"></i> Vorschau wird ermittelt…
                    </div>
                } @else if (previewError) {
                    <div class="text-red-500">
                        <i class="fa-regular fa-circle-exclamation"></i> {{ previewError }}
                    </div>
                } @else if (previewResult !== null && previewResult !== undefined) {
                    <div class="surface-100 border-round p-3">
                        <div class="text-color-secondary mb-1">Ergebnis</div>
                        <pre class="m-0 text-color">{{ previewResult | json }}</pre>
                    </div>
                } @else if (!areMandatoryParametersFilled()) {
                    <div class="text-color-secondary">
                        <i class="fa-regular fa-circle-info"></i> Bitte füllen Sie alle Pflichtparameter aus.
                    </div>
                }
            </p-card>
        }
    </div>
}
